import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, Upload, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function AgentRegister() {
  const [form, setForm] = useState({
    name:"", email:"", phone:"", password:"", agent_type:"Loan Agent",
    pan:"", aadhaar_last4:"", kyc_doc_base64:"", kyc_doc_name:"",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2_000_000) { toast.error("File too large (max 2 MB)"); return; }
    const r = new FileReader(); r.onload = () => setForm({ ...form, kyc_doc_base64: String(r.result || ""), kyc_doc_name: f.name }); r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/agents/register", form);
      setResult(data);
      toast.success("Application submitted — awaiting admin approval");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="card-flat p-10 max-w-md text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-saffron/15 grid place-items-center"><CheckCircle2 className="h-8 w-8 text-saffron"/></div>
          <h2 className="font-display text-2xl font-bold mt-4">Application submitted!</h2>
          <p className="text-ink-muted mt-3 leading-relaxed">
            Your registration is now <strong>pending admin approval</strong>. You will be able to sign in once an administrator verifies and approves your application.
          </p>
          <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wider text-ink-muted">Your reference code</div>
            <div className="font-mono font-extrabold text-3xl text-saffron mt-1 tracking-wider" data-testid="agent-reg-code">{result.code}</div>
            <div className="text-xs text-ink-muted mt-2">Save this — you'll use it to sign in after approval.</div>
          </div>
          <Button data-testid="agent-reg-go-login" className="btn-outline-navy rounded-full mt-6" onClick={() => nav("/agent/login")}>Back to login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto card-flat p-7 sm:p-10" data-testid="agent-register-form">
        <Link to="/" data-testid="agent-register-back" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted hover:text-saffron transition-colors mb-5">
          <ArrowLeft className="h-3.5 w-3.5"/> Back to home
        </Link>
        <div className="overline">Agent registration</div>
        <h1 className="font-display text-3xl font-extrabold mt-2">Join the TARA agent network.</h1>
        <p className="text-sm text-ink-muted mt-2">Complete KYC and submit your application. Once an administrator approves your profile, your agent code will become active and you'll be able to sign in.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Full name"><Input data-testid="reg-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} required/></F>
            <F label="Agent type">
              <Select value={form.agent_type} onValueChange={v=>setForm({...form, agent_type:v})}>
                <SelectTrigger data-testid="reg-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Loan Agent">Loan Agent</SelectItem>
                  <SelectItem value="Insurance Agent">Insurance Agent</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Email"><Input data-testid="reg-email" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} required/></F>
            <F label="Phone"><Input data-testid="reg-phone" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} required/></F>
            <F label="PAN"><Input data-testid="reg-pan" value={form.pan} onChange={e=>setForm({...form, pan:e.target.value.toUpperCase()})} placeholder="ABCDE1234F"/></F>
            <F label="Aadhaar last 4"><Input data-testid="reg-aad" value={form.aadhaar_last4} maxLength={4} onChange={e=>setForm({...form, aadhaar_last4:e.target.value})} placeholder="••••"/></F>
            <F label="Password"><Input data-testid="reg-pw" type="password" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} required/></F>
          </div>

          <F label="KYC document (PAN/Aadhaar — PDF or image, max 2 MB)">
            <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl p-5 cursor-pointer hover:border-saffron hover:bg-saffron/5 transition-colors">
              <Upload className="h-5 w-5 text-saffron"/>
              <span className="text-sm">{form.kyc_doc_name || "Choose file"}</span>
              <input data-testid="reg-kyc" type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onFile}/>
            </label>
          </F>

          <Button data-testid="reg-submit" type="submit" disabled={busy} className="btn-saffron rounded-full w-full h-12 mt-2">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Submit application
          </Button>
          <div className="text-center text-sm text-ink-muted">Already registered? <Link to="/agent/login" className="text-saffron font-semibold">Sign in</Link></div>
        </form>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
